import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Row, Col, Pagination, Card, Alert, Badge } from 'react-bootstrap';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';
import api from '../../api';
import Processo from '../../model/processo/Processo';
import { ProcessoViewModel } from '../../viewmodel/ProcessoViewModel';



interface AuditoriaVotacao {
  votacaoId: string;
  dataModificacao: string;
  tipoModificacao: string;
  ator: string;
  voto: string;
  justificativa: string;
  dataHoraVotacao: string;
  candidatoNome: string;
  candidatoCpf: string;
  candidatoInscricao: string;
  membroNome: string;
  membroCpf: string;
  membroVinculo: string;
  membroPapel: string;
  processoNome: string;
  processoIdentificador: string;
  bancaStatus: string;
  tipoBanca: string;
  editalNome?: string;
  numeroChamada?: number;
}

interface AuditoriaResponse {
  content: AuditoriaVotacao[];
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  number: number;
  size: number;
}

const AuditoriaVotacao: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [data, setData] = useState<AuditoriaResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const [filtros, setFiltros] = useState({
    candidatoNome: '',
    voto: '',
    editalId: '',
    numeroChamada: '',
    dataInicio: '',
    dataFim: ''
  });
  const [searchTerms, setSearchTerms] = useState({ edital: '' });
  const [suggestions, setSuggestions] = useState({ editais: [] });
  const [loadingSuggestions, setLoadingSuggestions] = useState({ editais: false });

  useEffect(() => {
    setBreadcrumbs([
      { path: '/', breadcrumb: '🏠' },
      { path: '/auditoria-heteroidentificacao', breadcrumb: 'Auditoria' }
    ]);
  }, [setBreadcrumbs]);



  const searchEditaisAuditoria = async (term: string) => {
    if (!term || term.length < 2) {
      setSuggestions(prev => ({ ...prev, editais: [] }));
      return;
    }
    setLoadingSuggestions(prev => ({ ...prev, editais: true }));
    try {
      const response = await api.get(`/editais/auditoria/search?q=${encodeURIComponent(term)}&limit=10`);
      setSuggestions(prev => ({ ...prev, editais: response.data }));
    } catch (error) {
      console.error('Erro ao buscar editais para auditoria:', error);
    } finally {
      setLoadingSuggestions(prev => ({ ...prev, editais: false }));
    }
  };

  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  const debouncedSearchEditaisAuditoria = debounce(searchEditaisAuditoria, 300);

  const fetchAuditoriaVotacoes = async () => {
    setLoading(true);
    const params = new URLSearchParams({
      ...Object.fromEntries(Object.entries(filtros).filter(([_, v]) => v)),
      page: page.toString(),
      size: size.toString()
    });

    try {
      const response = await api.get(`/votacoes/auditoria?${params}`);
      setData(response.data);
    } catch (error) {
      console.error('Erro ao buscar histórico de auditoria:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditoriaVotacoes();
  }, [page, size]);

  const handleFiltroChange = (campo: string, valor: string) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const handleBuscar = () => {
    setPage(0);
    fetchAuditoriaVotacoes();
  };

  const exportarPDFAuditoria = async () => {
    if (!data?.content.length) return;
    
    const params = new URLSearchParams({
      ...Object.fromEntries(Object.entries(filtros).filter(([_, v]) => v)),
      pdf: 'true'
    });
    
    try {
      const response = await api.get(`/votacoes/auditoria?${params}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `auditoria-heteroidentificacoes-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
    } catch (error) {
      console.error('Erro ao gerar PDF de auditoria:', error);
    }
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="container mt-4">
      <div className="mb-4">
        <h2>Histórico de Heteroidentificações</h2>
      </div>
      
      {data?.content.length && (
        <Button 
          variant="danger" 
          onClick={exportarPDFAuditoria}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 1000,
            borderRadius: '50px',
            padding: '12px 20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          📄 PDF
        </Button>
      )}
      
      <Alert variant="info">
        <strong>Sobre este relatório:</strong> Este histórico contém todas as votações realizadas nas bancas de heteroidentificação, 
        incluindo dados dos candidatos, membros avaliadores e decisões tomadas. Os dados podem ser utilizados para 
        fins administrativos e jurídicos.
      </Alert>

      <Card className="mb-3">
        <Card.Header><strong>Filtros</strong></Card.Header>
        <Card.Body className="pb-2">
          <Form>
            <Row className="g-2">

              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Edital</Form.Label>
                  <div className="position-relative">
                    <Form.Control 
                      size="sm"
                      type="text"
                      placeholder="Buscar edital..."
                      value={searchTerms.edital}
                      onChange={(e) => {
                        setSearchTerms(prev => ({ ...prev, edital: e.target.value }));
                        debouncedSearchEditaisAuditoria(e.target.value);
                      }}
                    />
                    {loadingSuggestions.editais && (
                      <div className="position-absolute end-0 top-50 translate-middle-y me-2">
                        <div className="spinner-border spinner-border-sm" />
                      </div>
                    )}
                    {suggestions.editais.length > 0 && (
                      <div className="position-absolute w-100 bg-white border rounded shadow-sm" style={{zIndex: 1000, top: '100%'}}>
                        {suggestions.editais.map((e: any) => (
                          <div 
                            key={e.id} 
                            className="p-2 border-bottom"
                            style={{cursor: 'pointer'}}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            onClick={() => {
                              handleFiltroChange('editalId', e.id);
                              setSearchTerms(prev => ({ ...prev, edital: e.nome }));
                              setSuggestions(prev => ({ ...prev, editais: [] }));
                            }}
                          >
                            <strong>{e.nome}</strong>
                            {e.hasChamadas && <Badge bg="info" className="ms-2">Com chamadas</Badge>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Chamada</Form.Label>
                  <Form.Control 
                    size="sm"
                    type="number"
                    placeholder="1, 2, 3..."
                    value={filtros.numeroChamada}
                    onChange={(e) => handleFiltroChange('numeroChamada', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Candidato</Form.Label>
                  <Form.Control 
                    size="sm"
                    type="text"
                    placeholder="Nome do candidato..."
                    value={filtros.candidatoNome} 
                    onChange={(e) => handleFiltroChange('candidatoNome', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-2">
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Resultado</Form.Label>
                  <Form.Select 
                    size="sm"
                    value={filtros.voto} 
                    onChange={(e) => handleFiltroChange('voto', e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="VALIDADO">Aprovados</option>
                    <option value="NAO_VALIDADO">Não validados</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Data Inicial</Form.Label>
                  <Form.Control 
                    size="sm"
                    type="date" 
                    value={filtros.dataInicio}
                    onChange={(e) => handleFiltroChange('dataInicio', e.target.value + 'T00:00:00')}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Data Final</Form.Label>
                  <Form.Control 
                    size="sm"
                    type="date" 
                    value={filtros.dataFim}
                    onChange={(e) => handleFiltroChange('dataFim', e.target.value + 'T23:59:59')}
                  />
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <Button size="sm" variant="primary" onClick={handleBuscar} className="w-100">
                  🔍 Buscar
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center p-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead className="table-dark">
              <tr>
                <th>Data/Hora</th>
                <th>Candidato</th>
                <th>Edital</th>
                <th>Membro Avaliador</th>
                <th>Resultado</th>
                <th>Justificativa</th>
                <th>Tipo de Banca</th>
              </tr>
            </thead>
            <tbody>
              {data?.content.map((item) => (
                <tr key={item.votacaoId}>
                  <td style={{minWidth: '140px'}}>
                    {formatarData(item.dataHoraVotacao)}
                  </td>
                  <td>
                    <strong>{item.candidatoNome}</strong><br/>
                    <small className="text-muted">
                      CPF: {item.candidatoCpf}<br/>
                      Inscrição: {item.candidatoInscricao}
                    </small>
                  </td>
                  <td>
                    {item.editalNome && (
                      <><strong>{item.editalNome}</strong></>
                    )}
                    {item.numeroChamada && (
                      <><br/><small className="text-info">Chamada {item.numeroChamada}</small></>
                    )}
                  </td>
                  <td>
                    <strong>{item.membroNome}</strong><br/>
                    <small className="text-muted">
                      {item.membroVinculo} - {item.membroPapel}<br/>
                      CPF: {item.membroCpf}
                    </small>
                  </td>
                  <td>
                    <Badge bg={item.voto === 'VALIDADO' ? 'success' : 'danger'}>
                      {item.voto === 'VALIDADO' ? '✓ Aprovado' : '✗ Não validado'}
                    </Badge>
                  </td>
                  <td style={{maxWidth: '200px'}}>
                    <small>{item.justificativa || 'Não informada'}</small>
                  </td>
                  <td>
                    <Badge bg="secondary">{item.tipoBanca}</Badge><br/>
                    <small className="text-muted">{item.bancaStatus}</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {data && data.totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <div className="d-flex align-items-center">
                <span className="me-2">Registros por página:</span>
                <Form.Select 
                  value={size} 
                  onChange={(e) => {
                    setSize(Number(e.target.value));
                    setPage(0);
                  }}
                  style={{width: '80px'}}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
              </div>
              
              <Pagination className="mb-0">
                <Pagination.First 
                  onClick={() => setPage(0)} 
                  disabled={data.first} 
                />
                <Pagination.Prev 
                  onClick={() => setPage(page - 1)} 
                  disabled={data.first} 
                />
                
                {Array.from({ length: Math.min(5, data.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (data.totalPages <= 5) {
                    pageNum = i;
                  } else if (page < 3) {
                    pageNum = i;
                  } else if (page > data.totalPages - 4) {
                    pageNum = data.totalPages - 5 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  
                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === page}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum + 1}
                    </Pagination.Item>
                  );
                })}
                
                <Pagination.Next 
                  onClick={() => setPage(page + 1)} 
                  disabled={data.last} 
                />
                <Pagination.Last 
                  onClick={() => setPage(data.totalPages - 1)} 
                  disabled={data.last} 
                />
              </Pagination>
              
              <div className="text-muted">
                Página {page + 1} de {data.totalPages}
              </div>
            </div>
          )}

          {data && (
            <div className="mt-3 text-center">
              <Badge bg="info" className="fs-6">
                Exibindo {data.content.length} de {data.totalElements} registros
              </Badge>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditoriaVotacao;
